/**
 * Dettaglio DDT (Fase 2.2 catena documentale).
 * Testata + dati trasporto + righe. Conferma (emesso in bozza) con scarico
 * magazzino automatico. Link all'ordine/preventivo di origine.
 * UI solo da @floraltech/ui.
 */
import { Button, Card, CardContent, CardHeader, CardTitle } from '@floraltech/ui';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface Riga {
  id: string;
  descrizione: string;
  quantita: number | string;
  prezzo_unitario: number | string;
  iva_perc: number | string | null;
  importo_riga: number | string;
  articolo_id: string | null;
}

interface DDT {
  id: string;
  numero: string;
  anno: number;
  direzione: string;
  stato: string;
  data_ddt: string;
  controparte_denominazione: string | null;
  controparte_piva: string | null;
  ordine_id: string | null;
  ordine_tipo: string | null;
  preventivo_id: string | null;
  causale_trasporto_desc: string | null;
  aspetto_beni: string | null;
  porto: string | null;
  trasporto_a_cura: string | null;
  colli: number | null;
  peso_kg: number | string | null;
  vettore: string | null;
  valuta: string;
  importo_totale: number | string;
  note: string | null;
  righe: Riga[];
}

const fmtEUR = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });

export function DDTDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [ddt, setDdt] = useState<DDT | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!id) return;
    try {
      const res = await fetch(`/api/v1/ddt/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` },
      });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      setDdt(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore');
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleConferma = async () => {
    if (!id) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/ddt/${id}/conferma`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.detail ?? `Errore ${res.status}`);
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore conferma');
    } finally {
      setBusy(false);
    }
  };

  const generaFattura = async () => {
    if (!id) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/ddt/${id}/genera-fattura`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.detail ?? `Errore ${res.status}`);
      }
      const data = await res.json();
      navigate(`/documentale/fatture/${data.fattura_id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore generazione fattura');
    } finally {
      setBusy(false);
    }
  };

  if (error && !ddt) return <p className="p-6 text-sm text-destructive">{error}</p>;
  if (!ddt) return <p className="p-6 text-sm text-muted-foreground">Caricamento...</p>;

  const puoConfermare = ddt.direzione === 'emesso' && ddt.stato === 'bozza';
  const puoFatturare = ddt.direzione === 'emesso';

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          Indietro
        </Button>
        <h1 className="font-outfit text-2xl font-semibold">DDT {ddt.numero}</h1>
        <span className="rounded bg-neutral-200 px-2 py-1 text-xs dark:bg-neutral-700">
          {ddt.direzione}
        </span>
        <span className="rounded bg-neutral-200 px-2 py-1 text-xs dark:bg-neutral-700">
          {ddt.stato}
        </span>
        {puoConfermare && (
          <Button size="sm" onClick={handleConferma} disabled={busy}>
            {busy ? 'Conferma...' : 'Conferma e scarica magazzino'}
          </Button>
        )}
        {puoFatturare && (
          <Button size="sm" variant="secondary" onClick={generaFattura} disabled={busy}>
            {busy ? 'Generazione...' : 'Genera Fattura'}
          </Button>
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <Card>
        <CardHeader>
          <CardTitle>{ddt.direzione === 'emesso' ? 'Destinatario' : 'Mittente'}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
            <div>
              <p className="text-muted-foreground">Denominazione</p>
              <p>{ddt.controparte_denominazione ?? '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">P.IVA</p>
              <p>{ddt.controparte_piva ?? '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Data</p>
              <p>{ddt.data_ddt}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Totale</p>
              <p className="font-medium">{fmtEUR.format(Number(ddt.importo_totale))}</p>
            </div>
            {ddt.ordine_id && (
              <div>
                <p className="text-muted-foreground">Ordine di origine</p>
                {ddt.ordine_tipo === 'vendita' ? (
                  <button
                    className="text-sm text-blue-700 hover:underline"
                    onClick={() => navigate(`/ordini-vendita/${ddt.ordine_id}`)}
                  >
                    Apri ordine vendita
                  </button>
                ) : (
                  <p>Ordine acquisto</p>
                )}
              </div>
            )}
            {ddt.preventivo_id && (
              <div>
                <p className="text-muted-foreground">Preventivo</p>
                <button
                  className="text-sm text-blue-700 hover:underline"
                  onClick={() => navigate(`/preventivi/${ddt.preventivo_id}`)}
                >
                  Apri preventivo
                </button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Dati trasporto</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <div>
              <p className="text-muted-foreground">Causale</p>
              <p>{ddt.causale_trasporto_desc ?? '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Aspetto beni</p>
              <p>{ddt.aspetto_beni ?? '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Porto</p>
              <p>{ddt.porto ?? '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Trasporto a cura di</p>
              <p>{ddt.trasporto_a_cura ?? '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Colli</p>
              <p>{ddt.colli ?? '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Peso (kg)</p>
              <p>{ddt.peso_kg != null ? String(ddt.peso_kg) : '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Vettore</p>
              <p>{ddt.vettore ?? '-'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Righe</CardTitle>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="pb-2">Descrizione</th>
                <th className="pb-2">Mag.</th>
                <th className="pb-2">Qta</th>
                <th className="pb-2">Prezzo</th>
                <th className="pb-2">Importo</th>
              </tr>
            </thead>
            <tbody>
              {ddt.righe.map((r) => (
                <tr key={r.id} className="border-t">
                  <td className="py-1">{r.descrizione}</td>
                  <td className="py-1">{r.articolo_id ? 'si' : '-'}</td>
                  <td className="py-1">{String(r.quantita)}</td>
                  <td className="py-1">{fmtEUR.format(Number(r.prezzo_unitario))}</td>
                  <td className="py-1">{fmtEUR.format(Number(r.importo_riga))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

export default DDTDetail;
